import { createContext, useCallback, useContext, useState } from 'react';
import FinishSigningModal from 'components/modals/finish-signing-modal/FinishSigningModal';
import { nanoid } from 'nanoid';

/**
 * Parameters of the FinishSigningModal function.
 */
export interface IFinishSigningModalParams {
  kepMessageUuid: string;
  message: string | JSX.Element;
}

/**
 * FinishSigningModal function.
 */
export type IFinishSigningModal = (params: IFinishSigningModalParams) => void;

/**
 * Typedef for the FinishSigningModalProvider context.
 */
export interface IFinishSigningModalContext {
  finishSigningModal: IFinishSigningModal;
}

/**
 * The finishSigningModal context.
 */
const FinishSigningModalContext = createContext<IFinishSigningModalContext>(undefined!);

/**
 * Provides the FinishSigningModalContext.
 */
export default function FinishSigningModalProvider({ children }: { children: React.ReactNode }) {
  const [opened, setOpened] = useState(false);
  const [key, setKey] = useState(nanoid);

  const [params, setParams] = useState<IFinishSigningModalParams>({
    kepMessageUuid: '',
    message: '',
  });

  const finishSigningModal: IFinishSigningModal = useCallback((params) => {
    setParams(params);
    setOpened(true);
    setKey(nanoid());
  }, []);

  return (
    <FinishSigningModalContext.Provider value={{ finishSigningModal }}>
      {children}
      <FinishSigningModal {...params} key={key} opened={opened} onClose={() => setOpened(false)} />
    </FinishSigningModalContext.Provider>
  );
}

/**
 * Uses the FinishSigningModalContext.
 */
export function useFinishSigningModal() {
  return useContext(FinishSigningModalContext);
}
